// src/pages/supervisor/SupervisionCapacity.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    useTheme,
    Alert,
    CircularProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    LinearProgress,
    Stack,
    Chip,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Slider,
    Skeleton,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    TrendingUp,
    CheckCircle,
    Warning,
    PriorityHigh,
    People,
    AccessTime,
    BarChart,
    Info,
    HolidayVillage,
    Settings,
    Speed,
    Timeline,
    Error as ErrorIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISupervisionCapacityMetrics,
    APISupervisionCapacityRecommendations,
    APISupervisionCapacityUpdate,
    APISupervisionCapacityRequestChange,
    APISupervisionCapacityRequestLeave
} from '../../apiServices/APIs';
import { 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const SupervisionCapacity = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [historicalData, setHistoricalData] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    
    // Dialog states
    const [openCapacityDialog, setOpenCapacityDialog] = useState(false);
    const [openChangeRequestDialog, setOpenChangeRequestDialog] = useState(false);
    const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
    
    // Form states
    const [capacityForm, setCapacityForm] = useState({ 
        max_capacity: 10, 
        meeting_hours: 0, 
        notes: '' 
    });
    const [changeRequestForm, setChangeRequestForm] = useState({ 
        requested_capacity: 0, 
        reason: '' 
    });
    const [leaveForm, setLeaveForm] = useState({ 
        start_date: '', 
        end_date: '', 
        reason: '' 
    });
    
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Fetch all data
    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchMetrics(),
                fetchRecommendations()
            ]);
        } catch (err) {
            setError("Failed to load capacity data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Fetch metrics data
    const fetchMetrics = async () => {
        const result = await apiService.listById(APISupervisionCapacityMetrics, supervisorId);
        if (result.success && result.data && isMounted.current) {
            setMetrics(result.data.current);
            setHistoricalData(result.data.historical || []);
        } else if (!result.success) {
            console.warn("No metrics data found:", result.message);
        }
    };

    // Fetch recommendations
    const fetchRecommendations = async () => {
        const result = await apiService.listById(APISupervisionCapacityRecommendations, supervisorId);
        if (result.success && result.data && isMounted.current) {
            setRecommendations(result.data);
        }
    };

    // Update capacity settings
    const handleUpdateCapacity = async () => {
        if (!metrics) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('metric_id', metrics.id);
            formData.append('max_capacity', capacityForm.max_capacity);
            formData.append('meeting_hours', capacityForm.meeting_hours);
            if (capacityForm.notes) formData.append('notes', capacityForm.notes);
            
            const result = await apiService.update(APISupervisionCapacityUpdate, formData);
            
            if (result && result.success) {
                showToast("Success", "Capacity settings updated", "success");
                await fetchMetrics();
                handleCloseCapacityDialog();
            } else {
                showToast("Error", result.message || "Failed to update capacity", "error");
            }
        } catch (error) {
            showToast("Error", "Failed to update capacity", "error");
        } finally {
            setActionLoading(false);
        }
    };

    // Request capacity change
    const handleRequestChange = async () => {
        if (!metrics) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('metric_id', metrics.id);
            formData.append('requested_capacity', changeRequestForm.requested_capacity);
            formData.append('reason', changeRequestForm.reason);
            
            const result = await apiService.update(APISupervisionCapacityRequestChange, formData);
            
            if (result && result.success) {
                showToast("Success", "Change request submitted", "success");
                await fetchMetrics();
                handleCloseChangeRequestDialog();
            } else {
                showToast("Error", result.message || "Failed to submit request", "error");
            }
        } catch (error) {
            showToast("Error", "Failed to submit request", "error");
        } finally {
            setActionLoading(false);
        }
    };

    // Request research leave
    const handleRequestLeave = async () => {
        if (!metrics) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('metric_id', metrics.id);
            formData.append('start_date', leaveForm.start_date);
            formData.append('end_date', leaveForm.end_date);
            formData.append('reason', leaveForm.reason);
            
            const result = await apiService.update(APISupervisionCapacityRequestLeave, formData);
            
            if (result && result.success) {
                showToast("Success", "Leave request submitted", "success");
                await fetchMetrics();
                handleCloseLeaveDialog();
            } else {
                showToast("Error", result.message || "Failed to submit leave request", "error");
            }
        } catch (error) {
            showToast("Error", "Failed to submit leave request", "error");
        } finally {
            setActionLoading(false);
        }
    };

    // Dialog handlers
    const handleOpenCapacityDialog = () => {
        if (metrics) {
            setCapacityForm({
                max_capacity: metrics.max_capacity,
                meeting_hours: metrics.meeting_hours_per_week || 0,
                notes: metrics.notes || ''
            });
        }
        setOpenCapacityDialog(true);
    };

    const handleCloseCapacityDialog = () => {
        setOpenCapacityDialog(false);
        setCapacityForm({ max_capacity: 10, meeting_hours: 0, notes: '' });
    };

    const handleOpenChangeRequestDialog = () => {
        setChangeRequestForm({ 
            requested_capacity: metrics?.max_capacity + 2 || 12, 
            reason: '' 
        });
        setOpenChangeRequestDialog(true);
    };

    const handleCloseChangeRequestDialog = () => {
        setOpenChangeRequestDialog(false);
        setChangeRequestForm({ requested_capacity: 0, reason: '' });
    };

    const handleOpenLeaveDialog = () => {
        setLeaveForm({ start_date: '', end_date: '', reason: '' });
        setOpenLeaveDialog(true);
    };

    const handleCloseLeaveDialog = () => {
        setOpenLeaveDialog(false);
        setLeaveForm({ start_date: '', end_date: '', reason: '' });
    };

    // Helper functions
    const getCapacityGaugeColor = () => {
        if (!metrics) return 'error';
        const util = metrics.utilization_percentage;
        if (util >= 100) return 'error';
        if (util >= 85) return 'warning';
        if (util >= 70) return 'success';
        return 'info';
    };

    const getStatusIcon = () => {
        if (!metrics) return <Info />;
        switch(metrics.capacity_status) {
            case 'under_capacity': return <TrendingUp />;
            case 'optimal': return <CheckCircle />;
            case 'over_capacity': return <Warning />;
            case 'critical': return <PriorityHigh />;
            default: return <Info />;
        }
    };

    // Loading state
    if (loading) {
        return <LoadingState message="Loading Supervision Capacity..." variant="research" size="large" fullPage />;
    }

    // Error state
    if (error) {
        return (
            <ErrorState 
                error={error} 
                onRetry={fetchAllData} 
                title="Unable to Load Capacity Data" 
                variant="research" 
                fullPage 
                retryText="Retry Loading" 
            />
        );
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <SupervisorBar notificationCount={39} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Speed /> Supervision Capacity Management
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ 
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>
                </Box>
                {/* No Metrics Found Message */}
                {!metrics ? (
                    <Paper sx={{ 
                        p: { xs: 4, sm: 6 }, 
                        textAlign: 'center', 
                        borderRadius: 3,
                        backgroundColor: 'white'
                    }}>
                        <ErrorIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '125rem', sm: '1.5rem' } }}>
                            No Capacity Data Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Your supervision capacity metrics will appear here once you start supervising students.
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={fetchAllData}
                            startIcon={<RefreshIcon />}
                            size={isMobile ? "small" : "medium"}
                        >
                            Refresh Data
                        </Button>
                    </Paper>
                ) : (
                    <>
                        {/* Current Capacity Overview Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            {/* Capacity Card */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ 
                                    height: '100%', 
                                    textAlign: 'center',
                                    bgcolor: theme.palette[metrics.capacity_status_color]?.light + '20' || theme.palette.grey[100],
                                    transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Avatar sx={{ 
                                            mx: 'auto', 
                                            mb: 2, 
                                            bgcolor: theme.palette[metrics.capacity_status_color]?.main || theme.palette.primary.main,
                                            width: { xs: 48, sm: 56 },
                                            height: { xs: 48, sm: 56 }
                                        }}>
                                            {getStatusIcon()}
                                        </Avatar>
                                        <Typography variant="h3" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {metrics.assigned_count}/{metrics.max_capacity}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Assigned Students
                                        </Typography>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={metrics.utilization_percentage} 
                                            color={getCapacityGaugeColor()} 
                                            sx={{ mt: 2, height: 8, borderRadius: 4 }} 
                                        />
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            {metrics.utilization_percentage}% utilized
                                        </Typography>
                                        <Chip 
                                            icon={getStatusIcon()} 
                                            label={metrics.formatted_capacity_status} 
                                            color={metrics.capacity_status_color} 
                                            sx={{ mt: 2 }}
                                            size={isMobile ? "small" : "medium"}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Meeting Hours Card */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ 
                                    height: '100%', 
                                    textAlign: 'center', 
                                    transition: 'transform 0.2s', 
                                    '&:hover': { transform: 'translateY(-4px)' }
                                }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Avatar sx={{ 
                                            mx: 'auto', 
                                            mb: 2, 
                                            bgcolor: theme.palette.info.main,
                                            width: { xs: 48, sm: 56 },
                                            height: { xs: 48, sm: 56 }
                                        }}>
                                            <AccessTime />
                                        </Avatar>
                                        <Typography variant="h3" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1.5rem', sm: '2rem' }
                                        }}>
                                            {metrics.meeting_hours_per_week || 0}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Meeting Hours/Week
                                        </Typography>
                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                            {metrics.meeting_hours_per_week > 15 ? '⚠️ High meeting load' : '✓ Manageable load'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Leave Status Card */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ 
                                    height: '100%', 
                                    textAlign: 'center', 
                                    transition: 'transform 0.2s', 
                                    '&:hover': { transform: 'translateY(-4px)' },
                                    bgcolor: metrics.is_on_leave ? theme.palette.warning.light + '20' : 'transparent'
                                }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <Avatar sx={{ 
                                            mx: 'auto', 
                                            mb: 2, 
                                            bgcolor: metrics.is_on_leave ? theme.palette.warning.main : theme.palette.success.main,
                                            width: { xs: 48, sm: 56 },
                                            height: { xs: 48, sm: 56 }
                                        }}>
                                            <HolidayVillage />
                                        </Avatar>
                                        <Typography variant="h6" sx={{ 
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            {metrics.formatted_leave_status}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Leave Status
                                        </Typography>
                                        {metrics.leave_start_date && (
                                            <Typography variant="caption" sx={{ mt: 1, display: 'block', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                📅 {new Date(metrics.leave_start_date).toLocaleDateString()} - {new Date(metrics.leave_end_date).toLocaleDateString()}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Action Buttons */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    startIcon={<Settings />} 
                                    onClick={handleOpenCapacityDialog}
                                    sx={{ py: 1.5 }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Adjust Capacity
                                </Button>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    startIcon={<TrendingUp />} 
                                    onClick={handleOpenChangeRequestDialog}
                                    sx={{ py: 1.5 }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Request Change
                                </Button>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    startIcon={<HolidayVillage />} 
                                    onClick={handleOpenLeaveDialog}
                                    sx={{ py: 1.5 }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Request Leave
                                </Button>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    startIcon={<BarChart />} 
                                    sx={{ py: 1.5 }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    View Analytics
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Recommendations Section */}
                        {recommendations.length > 0 && (
                            <Paper sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                mb: 3, 
                                borderRadius: 3, 
                                bgcolor: theme.palette.info.light + '15',
                                backgroundColor: 'white'
                            }}>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 'bold', 
                                    mb: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                }}>
                                    <Info color="info" /> Recommendations
                                </Typography>
                                <Stack spacing={2}>
                                    {recommendations.map((rec, idx) => (
                                        <Alert 
                                            key={idx} 
                                            severity={rec.type} 
                                            action={rec.action && (
                                                <Button color="inherit" size="small">
                                                    {rec.action}
                                                </Button>
                                            )}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                                {rec.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {rec.message}
                                            </Typography>
                                        </Alert>
                                    ))}
                                </Stack>
                            </Paper>
                        )}

                        {/* Historical Data Table */}
                        {historicalData.length > 0 && (
                            <Paper sx={{ 
                                p: { xs: 2, sm: 3 }, 
                                borderRadius: 3,
                                backgroundColor: 'white',
                                overflow: 'auto'
                            }}>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 'bold', 
                                    mb: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                }}>
                                    <Timeline /> Historical Capacity Data
                                </Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table size="small" sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                                <TableCell><strong>Period</strong></TableCell>
                                                <TableCell align="center"><strong>Capacity</strong></TableCell>
                                                <TableCell align="center"><strong>Assigned</strong></TableCell>
                                                <TableCell align="center"><strong>Utilization</strong></TableCell>
                                                <TableCell align="center"><strong>Status</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {historicalData.map((item) => (
                                                <TableRow key={item.id} hover>
                                                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {item.academic_year} - {item.formatted_semester}
                                                    </TableCell>
                                                    <TableCell align="center">{item.max_capacity}</TableCell>
                                                    <TableCell align="center">{item.assigned_count}</TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 100, sm: 'auto' } }}>
                                                            <LinearProgress 
                                                                variant="determinate" 
                                                                value={item.utilization} 
                                                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                                                color={item.utilization >= 85 ? 'error' : item.utilization >= 70 ? 'warning' : 'success'}
                                                            />
                                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                {item.utilization}%
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            size="small" 
                                                            label={item.capacity_status?.replace('_', ' ')} 
                                                            color={item.capacity_status_color}
                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </>
                )}

                {/* Adjust Capacity Dialog */}
                <Dialog 
                    open={openCapacityDialog} 
                    onClose={handleCloseCapacityDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        Adjust Supervision Capacity
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ pt: 1 }}>
                            <Typography gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Maximum Students: <strong>{capacityForm.max_capacity}</strong>
                            </Typography>
                            <Slider 
                                value={capacityForm.max_capacity} 
                                onChange={(e, v) => setCapacityForm({ ...capacityForm, max_capacity: v })} 
                                min={1} 
                                max={30} 
                                step={1} 
                                valueLabelDisplay="auto" 
                                sx={{ mb: 3 }} 
                            />
                            <TextField 
                                fullWidth 
                                type="number" 
                                label="Meeting Hours per Week" 
                                sx={{ mb: 2 }} 
                                value={capacityForm.meeting_hours} 
                                onChange={(e) => setCapacityForm({ ...capacityForm, meeting_hours: parseFloat(e.target.value) })} 
                                inputProps={{ step: 0.5, min: 0 }}
                                size={isMobile ? "small" : "medium"}
                            />
                            <TextField 
                                fullWidth 
                                label="Notes (Optional)" 
                                multiline 
                                rows={3} 
                                value={capacityForm.notes} 
                                onChange={(e) => setCapacityForm({ ...capacityForm, notes: e.target.value })} 
                                placeholder="Any additional notes about this capacity adjustment..."
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Button onClick={handleCloseCapacityDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateCapacity} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Request Change Dialog */}
                <Dialog 
                    open={openChangeRequestDialog} 
                    onClose={handleCloseChangeRequestDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        Request Capacity Change
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ pt: 1 }}>
                            <Typography gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Requested Capacity: <strong>{changeRequestForm.requested_capacity}</strong>
                            </Typography>
                            <Slider 
                                value={changeRequestForm.requested_capacity} 
                                onChange={(e, v) => setChangeRequestForm({ ...changeRequestForm, requested_capacity: v })} 
                                min={1} 
                                max={30} 
                                step={1} 
                                valueLabelDisplay="auto" 
                                sx={{ mb: 3 }} 
                            />
                            <TextField 
                                fullWidth 
                                label="Reason for Request" 
                                multiline 
                                rows={4} 
                                required 
                                value={changeRequestForm.reason} 
                                onChange={(e) => setChangeRequestForm({ ...changeRequestForm, reason: e.target.value })} 
                                placeholder="Explain why you need to adjust your supervision capacity..."
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Button onClick={handleCloseChangeRequestDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleRequestChange} variant="contained" disabled={actionLoading || !changeRequestForm.reason} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={24} /> : 'Submit Request'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Request Leave Dialog */}
                <Dialog 
                    open={openLeaveDialog} 
                    onClose={handleCloseLeaveDialog} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        Request Research Leave
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ pt: 1 }}>
                            <TextField 
                                fullWidth 
                                type="date" 
                                label="Start Date" 
                                InputLabelProps={{ shrink: true }} 
                                sx={{ mb: 2 }} 
                                value={leaveForm.start_date} 
                                onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                            <TextField 
                                fullWidth 
                                type="date" 
                                label="End Date" 
                                InputLabelProps={{ shrink: true }} 
                                sx={{ mb: 2 }} 
                                value={leaveForm.end_date} 
                                onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                            <TextField 
                                fullWidth 
                                label="Reason for Leave" 
                                multiline 
                                rows={4} 
                                required 
                                value={leaveForm.reason} 
                                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} 
                                placeholder="Explain your research leave request..."
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Button onClick={handleCloseLeaveDialog} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button onClick={handleRequestLeave} variant="contained" disabled={actionLoading || !leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={24} /> : 'Submit Request'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default SupervisionCapacity;